/**
 * Bereich „Nicht zugeordnet" der Staffing-Maske (#158, Spec Abschnitt 5; #163 ohne Bedarfe):
 * Besetzungen ohne Position und SF-Projektbesetzungen ohne Besetzung.
 *
 * Zuordnen geht auf zwei gleichwertigen Wegen, beide mit `canResolve`:
 * - Auswahl „Position zuordnen…" + Knopf (SF-Zeilen mit dem Preisvorschlag vorausgewählt),
 * - Drag&Drop (natives HTML5 wie `phase-board.tsx`) eines Eintrags auf eine Positionszeile.
 *   Der gezogene Eintrag liegt im `ZiehKontext` (nicht im DataTransfer — dessen Inhalt ist
 *   während `dragover` in Browsern nicht lesbar); die Positionszeile ist das Ablegeziel.
 *
 * Endpunkt je Eintragsart (`useZuordnen`): SF-Zeile → Besetzung mit Verknüpfung anlegen,
 * Besetzung → Besetzung-Patch.
 *
 * Gibt es die Besetzung schon (vorgeschlagen, später taucht die SF-Zeile auf), wird die SF-Zeile
 * per „mit Besetzung verknüpfen…" an sie gehängt (Besetzung-Patch mit `sf_projektbesetzung_id`).
 */
import type { MaskeBesetzung, StaffingMaske } from '@projektverwaltung/shared';
import { type ReactNode, createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { formatPtPaar } from '../besetzung-pt.js';
import { EINGABE_RAHMEN } from '../inline-feld.js';
import { BesetzungZeile } from './besetzung-zeile.js';
import { type MaskeRechte, useMaske, useMaskeKnopf, usePreisText } from './maske-kontext.js';

type SfZeile = StaffingMaske['nicht_zugeordnet']['sf_projektbesetzungen'][number];

export type NzEintrag =
  | { art: 'sf'; sf_besetzung_id: string }
  | { art: 'besetzung'; staffing_besetzung_id: string };

type ZiehKontextWert = {
  gezogen: NzEintrag | null;
  setGezogen: (e: NzEintrag | null) => void;
  zuordnen: (e: NzEintrag, positionId: string) => void;
};

const ZiehKontext = createContext<ZiehKontextWert | null>(null);

/** Besetzungen und SF-Zeilen ordnet zu, wer `canResolve` hat. */
function darfZuordnen(rechte: MaskeRechte): boolean {
  return rechte.canResolve;
}

/** Für Positionszeilen: `null` außerhalb der Maske oder ohne jedes Zuordnungsrecht. */
export function useZiehen(): ZiehKontextWert | null {
  return useContext(ZiehKontext);
}

/** Ruft je Eintragsart den richtigen Endpunkt; Fehler als Toast, danach Neuladen. */
export function useZuordnen(): (e: NzEintrag, positionId: string) => void {
  const { projectId } = useMaske();
  const knopf = useMaskeKnopf();
  return useCallback(
    (e, positionId) => {
      knopf(() => {
        switch (e.art) {
          case 'sf':
            return api.createStaffingBesetzung(projectId, {
              beauftragte_position_id: positionId,
              sf_projektbesetzung_id: e.sf_besetzung_id,
            });
          case 'besetzung':
            return api.patchStaffingBesetzung(e.staffing_besetzung_id, {
              beauftragte_position_id: positionId,
            });
        }
      });
    },
    [projectId, knopf],
  );
}

/** Hält den gezogenen Eintrag für Ziehquelle (hier) und Ablegeziel (Positionszeile). */
export function ZiehBereich({ children }: { children: ReactNode }) {
  const { rechte } = useMaske();
  const zuordnen = useZuordnen();
  const [gezogen, setGezogen] = useState<NzEintrag | null>(null);
  if (!darfZuordnen(rechte)) return <>{children}</>;
  return (
    <ZiehKontext.Provider value={{ gezogen, setGezogen, zuordnen }}>
      {children}
    </ZiehKontext.Provider>
  );
}

const KNOPF = 'text-xs hover:underline';

/** Ziehbarer Kopf eines Eintrags; ohne Recht für diese Eintragsart nur Anzeige (kein `draggable`). */
function Ziehgriff({
  eintrag,
  testId,
  children,
}: {
  eintrag: NzEintrag;
  testId: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { rechte } = useMaske();
  const kontext = useZiehen();
  const ziehen = kontext && darfZuordnen(rechte) ? kontext : null;
  const ziehbar = ziehen
    ? {
        draggable: true,
        title: t('v2.staffing_maske.nz.ziehen', 'Auf eine Position ziehen'),
        onDragStart: (e: React.DragEvent) => {
          // Firefox startet ohne gesetzte Daten keinen Ziehvorgang.
          e.dataTransfer?.setData('text/plain', testId);
          if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
          ziehen.setGezogen(eintrag);
        },
        onDragEnd: () => ziehen.setGezogen(null),
      }
    : {};
  return (
    <div
      data-testid={testId}
      className="flex min-w-0 items-center gap-2"
      style={{ cursor: ziehen ? 'grab' : undefined }}
      {...ziehbar}
    >
      {ziehen && (
        <span aria-hidden className="shrink-0 text-xs" style={{ color: 'var(--ink-3)' }}>
          ⠿
        </span>
      )}
      {children}
    </div>
  );
}

/** Auswahl „Position zuordnen…" + Knopf; nur mit `canResolve` (`darfZuordnen`). */
function ZuordnenAuswahl({
  name,
  eintrag,
  vorschlag = null,
}: {
  name: string;
  eintrag: NzEintrag;
  vorschlag?: string | null;
}) {
  const { t } = useTranslation();
  const { rechte, positionOptionen } = useMaske();
  const zuordnen = useZuordnen();
  const [wert, setWert] = useState(vorschlag ?? '');
  if (!darfZuordnen(rechte)) return null;
  if (positionOptionen.length === 0) {
    return (
      <span className="text-xs" style={{ color: 'var(--ink-3)' }}>
        {t('v2.staffing_maske.nz.keine_position', 'Erst eine Position anlegen')}
      </span>
    );
  }
  return (
    <span className="flex shrink-0 items-center gap-2">
      <select
        aria-label={t('v2.staffing_maske.nz.zuordnen_von', { name })}
        className={`rounded px-1 py-0.5 text-xs ${EINGABE_RAHMEN}`}
        style={{ background: 'var(--surface)' }}
        value={wert}
        onChange={(e) => setWert(e.target.value)}
      >
        <option value="">{t('v2.staffing_maske.nz.zuordnen', 'Position zuordnen…')}</option>
        {positionOptionen.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {vorschlag !== null && wert === vorschlag && (
        <span className="text-[10px]" style={{ color: 'var(--ink-3)' }}>
          {t('v2.staffing_maske.nz.vorschlag', 'Vorschlag aus dem Preis')}
        </span>
      )}
      <button
        type="button"
        className={KNOPF}
        style={{ color: 'var(--primary)' }}
        disabled={wert === ''}
        aria-label={t('v2.staffing_maske.nz.zuordnen_knopf_von', { name })}
        onClick={() => zuordnen(eintrag, wert)}
      >
        {t('v2.staffing_maske.nz.zuordnen_knopf', 'Zuordnen')}
      </button>
    </span>
  );
}

/** Auswahl „mit Besetzung verknüpfen…" + Knopf; nur mit `canResolve`, nichts vorausgewählt. */
function VerknuepfenAuswahl({ zeile }: { zeile: SfZeile }) {
  const { t } = useTranslation();
  const { rechte, verknuepfbareBesetzungen } = useMaske();
  const knopf = useMaskeKnopf();
  const [wert, setWert] = useState('');
  if (!rechte.canResolve || verknuepfbareBesetzungen.length === 0) return null;
  const name = zeile.person_name;
  return (
    <span className="flex shrink-0 items-center gap-2">
      <select
        aria-label={t('v2.staffing_maske.nz.verknuepfen_von', { name })}
        className={`rounded px-1 py-0.5 text-xs ${EINGABE_RAHMEN}`}
        style={{ background: 'var(--surface)' }}
        value={wert}
        onChange={(e) => setWert(e.target.value)}
      >
        <option value="">
          {t('v2.staffing_maske.nz.verknuepfen', 'mit Besetzung verknüpfen…')}
        </option>
        {verknuepfbareBesetzungen.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={KNOPF}
        style={{ color: 'var(--primary)' }}
        disabled={wert === ''}
        aria-label={t('v2.staffing_maske.nz.verknuepfen_knopf_von', { name })}
        onClick={() =>
          knopf(() =>
            api.patchStaffingBesetzung(wert, { sf_projektbesetzung_id: zeile.sf_besetzung_id }),
          )
        }
      >
        {t('v2.staffing_maske.nz.verknuepfen_knopf', 'Verknüpfen')}
      </button>
    </span>
  );
}

function SfEintrag({ zeile }: { zeile: SfZeile }) {
  const { t } = useTranslation();
  const preisText = usePreisText();
  const eintrag: NzEintrag = { art: 'sf', sf_besetzung_id: zeile.sf_besetzung_id };
  const pt = formatPtPaar(zeile.beauftragte_pt, zeile.verbleibende_pt);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1 text-sm">
      <Ziehgriff eintrag={eintrag} testId={`nz-sf-${zeile.sf_besetzung_id}`}>
        <span
          className="shrink-0 rounded px-1 py-0.5 text-[10px]"
          style={{ color: 'var(--info-strong)', border: '1px solid var(--info-strong)' }}
        >
          {t('v2.staffing_maske.nz.art_sf', 'Salesforce')}
        </span>
        <span className="truncate" style={{ color: 'var(--ink)' }}>
          {zeile.person_name}
        </span>
        {zeile.ist_extern && (
          <span className="text-xs" style={{ color: 'var(--ink-3)' }}>
            {t('v2.staffing_maske.extern', 'extern')}
          </span>
        )}
        <span className="shrink-0 text-xs" style={{ color: 'var(--ink-3)' }}>
          <span title={t('v2.staffing_maske.sf.satz')}>
            {preisText(zeile.vk_stundensatz, 'stunde')}
          </span>{' '}
          ·{' '}
          {zeile.vk_nebenkostenpauschale !== null && (
            <>
              <span title={t('v2.staffing_maske.sf.nk')}>
                {preisText(zeile.vk_nebenkostenpauschale, 'tag')}
              </span>{' '}
              ·{' '}
            </>
          )}
          <span title={t('v2.staffing_maske.sf.pt')}>
            {pt.beauftragt} / {pt.verbleibend}
          </span>
        </span>
      </Ziehgriff>
      <ZuordnenAuswahl
        name={zeile.person_name}
        eintrag={eintrag}
        vorschlag={zeile.vorschlag_position_id}
      />
      <VerknuepfenAuswahl zeile={zeile} />
    </div>
  );
}

function BesetzungEintrag({ b }: { b: MaskeBesetzung }) {
  const { t } = useTranslation();
  const eintrag: NzEintrag = { art: 'besetzung', staffing_besetzung_id: b.staffing_besetzung_id };
  return (
    <BesetzungZeile
      b={b}
      griff={
        <Ziehgriff eintrag={eintrag} testId={`nz-besetzung-${b.staffing_besetzung_id}`}>
          <span className="shrink-0 text-xs" style={{ color: 'var(--ink-3)' }}>
            {t('v2.staffing_maske.nz.art_besetzung', 'Besetzung')}
          </span>
        </Ziehgriff>
      }
      positionsAuswahl={<ZuordnenAuswahl name={b.person_name} eintrag={eintrag} />}
    />
  );
}

export function NichtZugeordnet({ daten }: { daten: StaffingMaske['nicht_zugeordnet'] }) {
  const { t } = useTranslation();
  const anzahl = daten.besetzungen.length + daten.sf_projektbesetzungen.length;
  if (anzahl === 0) return null;
  const titel = t('v2.staffing_maske.nz.titel', 'Nicht zugeordnet');
  return (
    <section
      aria-label={titel}
      className="rounded p-2"
      style={{ border: '1px dashed var(--warn)', background: 'var(--surface-2)' }}
    >
      <div className="mb-1 flex items-center gap-2">
        <h4 className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>
          {titel} ({anzahl})
        </h4>
        <span className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
          {t(
            'v2.staffing_maske.nz.hinweis',
            'Per Auswahl oder durch Ziehen auf eine Positionszeile zuordnen.',
          )}
        </span>
      </div>
      <div className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
        {daten.besetzungen.map((b) => (
          <BesetzungEintrag key={b.staffing_besetzung_id} b={b} />
        ))}
        {daten.sf_projektbesetzungen.map((z) => (
          <SfEintrag key={z.sf_besetzung_id} zeile={z} />
        ))}
      </div>
    </section>
  );
}
